import type { ProcessedFormField } from "@/types/api/formSchema";

const TOOLTIP_MAP: Record<string, string> = {
  nature_of_business:
    "The primary category of goods or services this vendor provides.",
  type_of_product_service:
    "The specific product or service line this vendor supplies to Livspace.",
  tds_category:
    "The section under the Income Tax Act that governs TDS deduction on payments to this vendor.",
  tds_applicable:
    "Whether TDS is applicable on invoices from this vendor.",
  msme_registered:
    "Is this vendor registered under the MSME (Micro, Small, and Medium Enterprises) Act?",
  msme_registration_number:
    "UDYAM registration number issued by the Ministry of MSME. Format: UDYAM-XX-00-0000000.",
  msme_registration_date:
    "The date on which the MSME (UDYAM) registration was issued, as printed on the Udyam certificate.",
  master_rate_card_attachment:
    "Your rate card for the services you provide, as a .csv file. Required when Nature of Business is Services.",
  swift_bic:
    "SWIFT/BIC code for international bank transfers. 8 or 11 characters, alphanumeric.",
  gstin_primary:
    "15-character GST Identification Number for this facility (e.g., 29ABCDE1234A1Z5). First 2 digits derive the state. Entering a valid GSTIN fills in the registered address below.",
  po_notification_emails:
    "Email addresses to notify when a new Purchase Order is issued. Use the Add button below to include more than one.",
  escalation_emails:
    "Email addresses for issue escalation on this facility. Use the Add button below to include more than one.",
  vendor_invoice_portal_access_email:
    "Email address that will have login access to the Vendor Invoice Portal for this facility.",
  tars_production_process_management_access_email:
    "Email address that will have login access to the TARS production console for this facility.",

  purpose_of_onboarding:
    "What Livspace intends to buy from this vendor — the service or supply their work covers. For why a new vendor is being added at all, use New Vendor Onboarding Reason.",
  how_vendor_was_introduced_to_livspace:
    "Channel through which this vendor came to Livspace — referral, cold outreach, RFP, etc.",
  zone:
    "Operating zone assigned to this vendor. Determines which Category Manager is default-mapped for review.",
  super_category:
    "The Major category this vendor belongs to. Decides which questionnaire answers are asked below, and combines with Sub Category to form the vendor's profile type.",
  sub_category:
    "The Sub-category within the Major chosen above. The list changes with the Major; changing the Major clears this field.",

  // VM questionnaire (PM-2908). Which of these appear depends on the Major.
  new_vendor_onboarding_reason:
    "Why a new vendor is being added — expansion in capacity, churn out of an old vendor, or cost saving.",
  no_other_current_vendor:
    "Answer No if other vendors already provide this service; you will then be asked to justify adding another.",
  existing_vendor_justification:
    "Why this vendor is being onboarded even though existing vendors already provide the service.",
  vendor_selection_criteria:
    "What this vendor was judged on — price, capacity, quality, coverage, lead time, and so on.",
  past_experience_evaluated:
    "Confirm this vendor's past work or track record was reviewed before selection.",
  related_party_disclosure:
    "Answer Yes if this vendor is related to another Livspace vendor or to an employee; details are then required.",
  related_party_details:
    "Name the related vendor or employee and describe the relationship.",

  // KWS Installation conditional set.
  installation_vendor_training_provided:
    "Confirm installation training or an onboarding call was completed with this vendor. Evidence is then required.",
  installation_training_evidence_attachment:
    "Upload proof of the installation training or onboarding call — attendance, call recording, or signed note.",
  vendor_training_and_terms_alignment_attachment:
    "Upload the signed note confirming the vendor was taken through the training and agreed the commercial terms.",
  groove_type:
    "Groove the vendor cuts — Saw or Mill. Asked only for KWS Installation & Deep Cleaning vendors.",

  // Operations site visit, answered by the Vendor Manager (no separate Ops stage).
  ops_site_visit_made:
    "Confirm a site visit of this vendor's previous work was made. Feedback and photos are then required.",
  ops_site_visit_feedback:
    "What the site visit showed about the quality and finish of the vendor's previous work.",
  ops_work_recommendation:
    "How much work you recommend allocating to this vendor to begin with.",
  ops_site_visit_photos_attachment:
    "Upload photographs taken during the site visit of the vendor's previous work.",
  zone_override_if_applicable:
    "If the Zone selected above must be overridden for this vendor, note the justification. Leave blank otherwise.",
  credit_days:
    "Number of days from invoice date within which Livspace commits to pay this vendor.",
  payment_terms_pattern:
    "Split of invoice payment across tranches (e.g., 30-70 = 30% upfront + 70% on completion). Drives the derived Advance %.",
  first_tranche_payment_basis:
    "When the first tranche of payment is released — advance before milestone, on milestone, or on completion.",
  auto_invoice_validate:
    "If true, invoices from this vendor are auto-approved when they match PO within the ₹10 tolerance. Uncheck for manual VM approval on every invoice.",
  quotation_1:
    "Upload the first vendor quotation collected during price discovery.",
  quotation_2:
    "Upload the second vendor quotation collected during price discovery.",
  quotation_3:
    "Upload the third vendor quotation collected during price discovery.",
  is_vendor_onboarded_for_testing:
    "Mark Yes if this vendor is being onboarded for a limited trial before full go-live.",
  quotes_taken_over_email:
    "Confirm all three vendor quotations were formally requested and received over email (not verbal / phone).",
  three_vendor_quotes_available:
    "Confirm at least three competing vendor quotations were collected before selecting this vendor.",
  comparative_benchmarking_attachment:
    "Upload the comparative benchmarking sheet used to evaluate this vendor's quote against the others.",
  quote_justification_attachment:
    "Upload a signed note justifying why this vendor's quote was chosen over the alternatives.",
  l1_vendor_selected:
    "Confirm the vendor selected (L1) offers the lowest price among the three quotes. If L2/L3 was chosen, attach L1 Justification.",
  l1_justification_attachment:
    "Required only when the L1 vendor was NOT selected — upload the signed justification note explaining why.",
  vendor_agreement_attachment:
    "Optional — upload the signed vendor agreement if already in place.",
  category_manager_email:
    "Category Manager who will review this vendor after VM. Must have the CATEGORY_MANAGER role in Bouncer.",
  biz_fin_email:
    "Business Finance reviewer who will review this vendor after Category Manager. Must have the FINANCE_MANAGER role in Bouncer.",
};

export function resolveTooltip(
  field: Pick<ProcessedFormField, "name"> & { tooltip?: string },
): string | undefined {
  return field.tooltip || TOOLTIP_MAP[field.name] || undefined;
}
